import { Client } from "prpcow";

import { getPRPC, setPRPC } from "../../hooks/usePRPC";
import createLogger from "../../utils/logger.util";

import { baseSocket, prpcowConnectionKey } from "./domain/domain";

// Reconnection pause grows 300 ms x2 up to ~10 s with ±25% spread, so a
// server that is down is not hit every 300 ms by every dispatcher at once;
// a successful connection starts over (dev check F10).
const RECONNECTION_DELAY_MS = 300;
const RECONNECTION_DELAY_MAX_MS = 10_000;
const RECONNECTION_JITTER = 0.25;
const SOCKET_CLOSE_WAIT_MS = 1_500;
const MODEL_WAIT_MS = 12_000;
const WS_CONNECTING = 0;
const WS_OPEN = 1;
const WS_CLOSING = 2;
const WS_CLOSED = 3;

let reconnectionTimeoutId: ReturnType<typeof setTimeout> | number = -1;
let reconnectionAttempt = 0;

function nextReconnectionDelay() {
	const base = Math.min(
		RECONNECTION_DELAY_MS * 2 ** reconnectionAttempt,
		RECONNECTION_DELAY_MAX_MS,
	);
	reconnectionAttempt += 1;
	const spread = 1 + (Math.random() * 2 - 1) * RECONNECTION_JITTER;
	return Math.round(base * spread);
}
let modelWaitTimeoutId: ReturnType<typeof setTimeout> | number = -1;
let connectionGeneration = 0;
let isReconnect = false;
let shouldReconnect = true;
let activeClient: Client | null = null;
const trackedSockets = new Set<WebSocket>();
let socketToken: string | undefined;

export function setSocketToken(token?: string) {
	socketToken = token;
}

const logger = createLogger({ name: "prpcow::base" });

function clearReconnectTimer() {
	clearTimeout(reconnectionTimeoutId);
	reconnectionTimeoutId = -1;
}

function clearModelWaitTimer() {
	clearTimeout(modelWaitTimeoutId);
	modelWaitTimeoutId = -1;
}

function waitForSocketClose(socket: WebSocket): Promise<void> {
	return new Promise((resolve) => {
		if (!socket || socket.readyState === WS_CLOSED) {
			resolve();
			return;
		}

		const onClosed = () => {
			clearTimeout(timer);
			socket.removeEventListener("close", onClosed);
			resolve();
		};
		const timer = setTimeout(onClosed, SOCKET_CLOSE_WAIT_MS);
		socket.addEventListener("close", onClosed);

		if (
			socket.readyState === WS_OPEN ||
			socket.readyState === WS_CONNECTING
		) {
			try {
				socket.close(1000, "replaced");
			} catch {
				onClosed();
			}
		}
	});
}

async function destroyCurrentConnection() {
	clearModelWaitTimer();

	const prpc = getPRPC();
	if (prpc) {
		try {
			prpc.session?.close?.();
		} catch {
			// ignore
		}
		setPRPC(null);
	}

	if (activeClient?.websocket) {
		trackedSockets.add(activeClient.websocket as unknown as WebSocket);
	}
	activeClient = null;

	const sockets = Array.from(trackedSockets);
	trackedSockets.clear();
	await Promise.all(sockets.map((socket) => waitForSocketClose(socket)));
}

export function stopReconnect() {
	shouldReconnect = false;
	reconnectionAttempt = 0;
	socketToken = undefined;
	connectionGeneration += 1;
	clearReconnectTimer();
	destroyCurrentConnection();
}

function scheduleNewConnection(
	onConnected,
	onDisconnected,
	generation: number,
) {
	if (!shouldReconnect) {
		logger.info("Reconnect prevented by stopReconnect()");
		return;
	}
	if (generation !== connectionGeneration) {
		return;
	}

	const token = socketToken;
	clearReconnectTimer();
	logger.info("Creating a new prpcow connection with existing token", {
		hasToken: Boolean(token),
	});
	reconnectionTimeoutId = setTimeout(() => {
		if (!shouldReconnect || generation !== connectionGeneration) {
			return;
		}
		isReconnect = true;
		prpcow(onConnected, onDisconnected, token);
	}, nextReconnectionDelay());
}

export async function prpcow(onConnected, onDisconnected, token) {
	socketToken = token ?? socketToken;
	shouldReconnect = true;

	const generation = connectionGeneration + 1;
	connectionGeneration = generation;
	clearReconnectTimer();
	await destroyCurrentConnection();

	if (generation !== connectionGeneration) {
		return;
	}

	let disconnectHandled = false;
	const isCurrent = () => generation === connectionGeneration;

	const handleServerClosed = (error?: unknown) => {
		if (!isCurrent() || disconnectHandled) return;
		disconnectHandled = true;
		clearModelWaitTimer();
		if (error) {
			logger.error("Session connection failed:", error);
		}
		onDisconnected();
		logger.info("Destroying old session and opening a new one");
		scheduleNewConnection(onConnected, onDisconnected, generation);
	};

	try {
		const callback = (error, session) => {
			if (!isCurrent()) return;
			if (error) {
				return;
			}

			logger.info("prpcow handshake ready, waiting for theirsModel");

			const onModel = (model) => {
				if (!isCurrent()) return;
				clearModelWaitTimer();
				logger.info("Theirs model has been changed!", model);
				reconnectionAttempt = 0;
				onConnected(session, isReconnect);
				isReconnect = false;
			};

			session.on("theirsModelChange", onModel);
			session.on("close", () => {
				handleServerClosed(new Error("RPC session closed by server"));
			});
			session.on("error", (err) => {
				handleServerClosed(err);
			});

			if (session.theirsModel) {
				onModel(session.theirsModel);
			} else {
				modelWaitTimeoutId = setTimeout(() => {
					if (!isCurrent() || disconnectHandled) return;
					handleServerClosed(
						new Error("Timed out waiting for theirsModel"),
					);
				}, MODEL_WAIT_MS);
			}
		};

		const authToken = token ?? socketToken;
		if (!baseSocket) {
			handleServerClosed(new Error("BASE_SOCKET_HOST is not configured"));
			return;
		}

		logger.info("Opening prpcow websocket", {
			url: baseSocket,
			hasToken: Boolean(authToken),
			hasConnectionKey: Boolean(prpcowConnectionKey),
			generation,
		});

		const client = new Client(
			WebSocket,
			baseSocket,
			authToken ? [authToken] : [],
			{
				...(prpcowConnectionKey
					? { connectionKey: prpcowConnectionKey }
					: {}),
			},
			callback,
		);

		activeClient = client;
		const socket = client.websocket as unknown as WebSocket;
		trackedSockets.add(socket);
		if ("binaryType" in socket) {
			socket.binaryType = "arraybuffer";
		}

		const onSocketClose = (event?: CloseEvent) => {
			trackedSockets.delete(socket);
			if (!isCurrent()) return;
			logger.warn("Native websocket close", {
				code: event?.code,
				reason: event?.reason,
				wasClean: event?.wasClean,
				readyState: socket.readyState,
			});
			handleServerClosed(event ?? new Error("WebSocket closed"));
		};

		client.websocket.addEventListener("close", onSocketClose);

		logger.info("prpcow websocket readyState after open", {
			readyState: socket.readyState,
			states: { WS_CONNECTING, WS_OPEN, WS_CLOSING, WS_CLOSED },
		});

		await client.init();

		if (!isCurrent()) return;
		logger.info("prpcow init() finished");
	} catch (error) {
		handleServerClosed(error);
	}
}
